import { withBemMod } from '@bem-react/core';
import { useComponentRegistry } from '@bem-react/di';
import React, { useCallback } from 'react';
import { Omit } from 'utility-types';

import FiltersSelect from 'client/common/components/filters-select';
import HtmlContent from 'client/common/components/html-content';
import { useFilters } from 'client/common/hooks/useFilters';
import { IFilter, TFiltersByCategory, TFiltersCategory } from 'client/common/types';
import i18n from 'utils/i18n';

import { cnLevel, ILevelProps } from '..';

import './level_type_filters.css';

export interface ILevelTypeFiltersProps extends Omit<ILevelTypeFiltersProps, 'type'> {
    type?: 'filters';

    hasMore?: TFiltersByCategory;
    groups: Record<TFiltersCategory, IFilter[]>;
    order: TFiltersCategory[];

    text: string;
    title: string;
}

export const withLevelTypeFilters = withBemMod<ILevelTypeFiltersProps, ILevelProps>(
    cnLevel(),
    { type: 'filters' },
    (BaseLevel: React.FC<ILevelProps>) => props => {
        const {
            hasMore = {} as TFiltersByCategory,
            groups,
            order,
            text,
            title,
            ...otherProps
        } = props;

        const { Button, Icon } = useComponentRegistry('lego');
        const { add, value: filters, remove, removeAll } = useFilters();

        const disabled = convertToDisabled(groups, hasMore);

        const CloseIcon = useCallback((className: string) => (
            <Icon
                className={cnLevel('close-icon', [className])}
                type="close"
                />
        ), []);

        const addHandler = (category: TFiltersCategory) => (item: string) => {
            add(category, item);
        };

        const removeHandler = (category: TFiltersCategory, item: string) => () => {
            remove(category, item);
        };

        let hasAnyActiveFilter = false;

        return (
            <BaseLevel {...otherProps}>
                <h1 className={cnLevel('title')}>{title}</h1>
                <HtmlContent className={cnLevel('text')} content={text} />

                {order.map((category: TFiltersCategory) => (
                    <div className={cnLevel('select')} key={category}>
                        <FiltersSelect
                            add={addHandler(category)}
                            disabled={disabled[category]}
                            items={groups[category]}
                            placeholder={i18n({ keyset: 'solutions', key: category })}
                            value={filters[category] || []}
                            />
                    </div>
                ))}

                <div className={cnLevel('active-filters')}>
                    {order.map((category: TFiltersCategory) =>
                        (filters[category] || []).map((filterSlug: string) => {
                            const group = groups[category];
                            const filter = group.find(item => item.slug === filterSlug);

                            if (!filter) {
                                return null;
                            }

                            hasAnyActiveFilter = true;

                            return (
                                <Button
                                    className={cnLevel('active-filter')}
                                    key={filter.slug}
                                    iconRight={CloseIcon}
                                    onClick={removeHandler(category, filter.slug)}
                                    size="m"
                                    theme="normal"
                                    view="classic"
                                    >
                                    {filter.name}
                                </Button>
                            );
                        })
                    )}
                    {hasAnyActiveFilter && (
                        <Button
                            className={cnLevel('clear-all')}
                            onClick={removeAll}
                            size="m"
                            theme="normal"
                            view="classic"
                            >
                            {i18n({ keyset: 'filters', key: 'clear' })}
                        </Button>
                    )}
                </div>
            </BaseLevel>
        );
    }
);

function convertToDisabled(
    groups: Record<TFiltersCategory, IFilter[]>,
    hasMore: TFiltersByCategory
) {
    return Object.keys(groups)
        .reduce((acc, category: TFiltersCategory) => ({
            ...acc,
            [category]: groups[category].reduce((disabledInCategory: string[], { slug }: IFilter) =>
                ((hasMore && hasMore[category]) || []).includes(slug)
                    ? disabledInCategory
                    : disabledInCategory.concat(slug)
            , [])
        }), {}) as TFiltersByCategory;
}
